import { Menu } from 'antd';
import { Link } from 'react-router-dom';
import * as React from 'react';
import { FC, ReactNode, useMemo } from 'react';
import { MenuProps } from 'antd/lib/menu';
import SubMenu from 'antd/lib/menu/SubMenu';
import { MenuTheme } from 'antd/lib/menu/MenuContext';
import { useLocation } from 'react-router';
import styled from 'styled-components';

export interface MenuItem {
  key: string;
  icon: ReactNode;
  text: ReactNode;
  route?: string;
  children?: Omit<MenuItem, 'children'>[];
}

export type MenuItems = MenuItem[];

const getMenuSelectionProps = (
  currentRoute: string,
  menuItems: MenuItems,
  parentRouteKey?: string
): { selectedKeys?: string[]; defaultOpenKeys?: string[] } => {
  for (const item of menuItems) {
    if (item.route && currentRoute.startsWith(item.route)) {
      return {
        selectedKeys: [item.key],
        defaultOpenKeys: parentRouteKey ? [parentRouteKey] : [],
      };
    }
    if (item.children) {
      const childSelected = getMenuSelectionProps(
        currentRoute,
        item.children,
        item.key
      );

      if (childSelected.selectedKeys && childSelected.selectedKeys.length > 0) {
        return childSelected;
      }
    }
  }

  return {};
};

const MenuItem = styled(Menu.Item)`
  height: unset !important;
  min-height: 2.5rem;
  white-space: normal;
  align-items: center;
  padding-top: 0.5rem;
`;

const StyledSubMenu = styled(SubMenu).attrs({
  id: 'menu-item-users-management',
})`
  .ant-menu-submenu-title {
    #menu-item-users-management-title {
      display: contents;
    }
  }
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: baseline;
  padding: 0.5rem 0;
`;

const LinkIconContainer = styled.div`
  line-height: 1; // fixes ant inheritance
`;

const LinkLabel = styled.div<{ $collapsed?: boolean }>`
  line-height: 1.5;
  display: ${(props) => (props.$collapsed ? 'none' : 'inline')};
`;

export interface MenuPropsEx extends Omit<MenuProps, 'children'> {
  menuItems: MenuItems;
  theme?: MenuTheme;
  mode?: MenuProps['mode'];
  focusable?: boolean;
  routePrefix?: string;
  collapsed?: boolean;
}

export const MenuEx: FC<MenuPropsEx> = ({
  menuItems,
  routePrefix = '',
  collapsed,
  ...menuProps
}) => {
  const { pathname } = useLocation();
  const selectionProps = useMemo(
    () =>
      getMenuSelectionProps(
        pathname.substr(routePrefix?.length ?? 0),
        menuItems
      ),
    [menuItems, pathname, routePrefix]
  );

  return (
    <Menu
      theme="light"
      mode="inline"
      inlineCollapsed={collapsed}
      {...menuProps}
      {...selectionProps}
    >
      {menuItems.map(({ children, ...item }) => {
        if (children) {
          if (item.key === 'users-management') {
            return (
              <StyledSubMenu
                key={item.key}
                icon={item.icon}
                title={
                  <div id="menu-item-users-management-title">{item.text}</div>
                }
              >
                {children.map((childItem) => {
                  if (childItem.key === 'users-management/admins') {
                    return (
                      <MenuItem
                        id="menu-item-users-management-admins"
                        key={childItem.key}
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                        <StyledLink to={routePrefix + childItem.route!}>
                          <LinkIconContainer>
                            {childItem.icon}
                          </LinkIconContainer>
                          <LinkLabel>{childItem.text}</LinkLabel>
                        </StyledLink>
                      </MenuItem>
                    );
                  } else if (childItem.key === 'users-management/doctors') {
                    return (
                      <MenuItem
                        id="menu-item-users-management-doctors"
                        key={childItem.key}
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                        <StyledLink to={routePrefix + childItem.route!}>
                          <LinkIconContainer>
                            {childItem.icon}
                          </LinkIconContainer>
                          <LinkLabel>{childItem.text}</LinkLabel>
                        </StyledLink>
                      </MenuItem>
                    );
                  } else if (childItem.key === 'users-management/users') {
                    return (
                      <MenuItem
                        id="menu-item-users-management-users"
                        key={childItem.key}
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                        <StyledLink to={routePrefix + childItem.route!}>
                          <LinkIconContainer>
                            {childItem.icon}
                          </LinkIconContainer>
                          <LinkLabel>{childItem.text}</LinkLabel>
                        </StyledLink>
                      </MenuItem>
                    );
                  } else if (
                    childItem.key === 'users-management/medical-operators'
                  ) {
                    return (
                      <MenuItem
                        id="menu-item-users-management-medical-operators"
                        key={childItem.key}
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                        <StyledLink to={routePrefix + childItem.route!}>
                          <LinkIconContainer>
                            {childItem.icon}
                          </LinkIconContainer>
                          <LinkLabel>{childItem.text}</LinkLabel>
                        </StyledLink>
                      </MenuItem>
                    );
                  } else if (childItem.key === 'users-management/patients') {
                    return (
                      <MenuItem
                        id="menu-item-users-management-patients"
                        key={childItem.key}
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                        <StyledLink to={routePrefix + childItem.route!}>
                          <LinkIconContainer>
                            {childItem.icon}
                          </LinkIconContainer>
                          <LinkLabel>{childItem.text}</LinkLabel>
                        </StyledLink>
                      </MenuItem>
                    );
                  }
                  return null;
                })}
              </StyledSubMenu>
            );
          }
        } else {
          // Regular MenuItem
          let itemId = '';
          if (item.key === 'details') {
            itemId = 'menu-item-details';
          } else if (item.key === 'devices') {
            itemId = 'menu-item-devices';
          } else if (item.key === 'my-partners') {
            itemId = 'menu-item-my-partners';
          } else if (
            item.key === 'business-partners' ||
            item.key === 'businessPartners'
          ) {
            itemId = 'menu-item-business-partners';
          }

          return (
            <MenuItem
              id={itemId}
              key={item.key}
              title={collapsed ? String(item.text) : ''}
            >
              {/*eslint-disable-next-line @typescript-eslint/no-non-null-assertion*/}
              <StyledLink to={routePrefix + item.route!}>
                <LinkIconContainer>{item.icon}</LinkIconContainer>
                <LinkLabel $collapsed={collapsed}>{item.text}</LinkLabel>
              </StyledLink>
            </MenuItem>
          );
        }
        return null;
      })}
    </Menu>
  );
};
